import type { ComputedRef } from 'vue'
import type { TClinicDetail } from '~/types/clinic'
import {
  buildOpeningHoursSpecification,
  streetAddressWithoutDistrict,
} from '~/utils/clinicSeoMeta'

export function useClinicDetailSchema(clinic: ComputedRef<TClinicDetail | null | undefined>) {
  const config = useRuntimeConfig()
  const baseUrl = config.public.canonicalDomain || 'https://vetsy.ru'
  const route = useRoute()

  const schemaJson = computed(() => {
    const data = clinic.value
    if (!data) {
      return null
    }

    const path = route.path.endsWith('/') ? route.path : `${route.path}/`
    const clinicUrl = `${baseUrl}${path}`
    const cityName = typeof data.city === 'object' ? data.city.name : data.city
    const street = streetAddressWithoutDistrict(data.address)
    const isAlwaysOpen = data.tags?.some((tag) => /круглосуточ/i.test(tag))
    const hours = buildOpeningHoursSpecification(data.schedule, isAlwaysOpen)

    const veterinaryCare: Record<string, unknown> = {
      '@type': 'VeterinaryCare',
      '@id': `${clinicUrl}#org`,
      name: data.name,
      url: clinicUrl,
      description: data.about || undefined,
    }

    if (data.logo) {
      veterinaryCare.image = data.logo
      veterinaryCare.logo = data.logo
    }
    if (street || cityName) {
      veterinaryCare.address = {
        '@type': 'PostalAddress',
        streetAddress: street || undefined,
        addressLocality: cityName || undefined,
        addressCountry: 'RU',
      }
    }
    if (data.location?.latitude && data.location?.longitude) {
      veterinaryCare.geo = {
        '@type': 'GeoCoordinates',
        latitude: data.location.latitude,
        longitude: data.location.longitude,
      }
    }
    if (hours.length) {
      veterinaryCare.openingHoursSpecification = hours
    }

    const ratingValue = data.rating ? parseFloat(String(data.rating)) : 0
    const reviewCount = data.reviewCount ?? 0
    if (reviewCount > 0 && ratingValue > 0) {
      veterinaryCare.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: data.rating,
        reviewCount: String(reviewCount),
        bestRating: 5,
        worstRating: 1,
      }
    }

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Главная', item: `${baseUrl}/` },
            { '@type': 'ListItem', position: 2, name: 'Клиники', item: `${baseUrl}/clinics/` },
            {
              '@type': 'ListItem',
              position: 3,
              name: cityName,
              item: `${baseUrl}/clinics/${data.citySlug}/`,
            },
            { '@type': 'ListItem', position: 4, name: data.name },
          ],
        },
        veterinaryCare,
      ],
    }
  })

  useHead({
    script: [
      {
        type: 'application/ld+json',
                innerHTML: () => (schemaJson.value ? JSON.stringify(schemaJson.value) : ''),
            },
        ],
    })
}
